import { streamdownRehypePlugins } from '@heidi/answer-kit';
import { Badge } from '@heidi/design-system/badge';
import {
  TypographyCaption,
  TypographyP2,
  TypographyP2Bold,
  TypographyPBold,
} from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { cn, getFaviconUrl } from '@heidi/utils';
import {
  Calendar,
  CheckCircle2,
  Globe,
  Image as ImageIcon,
  MapPinned,
  User,
  type LucideIcon,
} from 'lucide-react';
import type { JSX, ReactNode } from 'react';
import { Streamdown } from 'streamdown';
import type { SimpleCitation } from '../../contracts';
import {
  getDomainFromUrl,
  getJurisdiction,
  isGuidelineSource,
  isPartnerCitation,
} from '../../contracts';
import { useIsPartnerNejmFiguresEnabled } from '../../flags';
import { AuthorityTierBadge } from './AuthorityTierBadge';
import { CiteScoreBadge } from './CiteScoreBadge';
import { useCitationDisplay } from './index';
import { IntegrationPartnerBadge } from './IntegrationPartnerBadge';
import { JurisdictionBadge } from './JurisdictionBadge';
import { resolvePartnerSourceSnippetText } from './partnerDisplaySummary';
import { PartnerFigurePreview } from './PartnerFigurePreview';
import { PartnerLogo } from './PartnerLogo';
import { useEvidenceAnswerPorts } from './ports';
import { RetrievedFromLabel } from './RetrievedFromLabel';
import { getFileTypeLabel } from './sourceIconUtils';

export type SourceCardVariant = 'v2' | 'v3';

/** Size for image thumbnails in pixels */
const IMAGE_THUMBNAIL_SIZE = 80;

function SourceIcon({
  isPartnerSource,
  partnerConfig,
  isFileSource,
  FileIcon,
  url,
  hasImageUrl,
  file_name,
  variant,
  wordmark = false,
}: {
  isPartnerSource: boolean;
  partnerConfig?: { brandColor: string; logo?: string; displayName: string } | null;
  isFileSource: boolean;
  FileIcon?: LucideIcon | null;
  url?: string;
  hasImageUrl: boolean;
  file_name?: string | null;
  variant: SourceCardVariant;
  /** True when the partner logo already contains the wordmark — render inline at wordmark proportions instead of in a fixed square frame. */
  wordmark?: boolean;
}) {
  if (hasImageUrl && variant === 'v2') {
    return (
      <div
        className="shrink-0 overflow-hidden rounded-md bg-background-tertiary"
        style={{ width: IMAGE_THUMBNAIL_SIZE, height: IMAGE_THUMBNAIL_SIZE }}
      >
        {/* eslint-disable-next-line @next/next/no-img-element -- Dynamic S3 presigned URLs */}
        <img
          src={url}
          alt={file_name || 'Image'}
          className="size-full object-cover"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
            const f = e.currentTarget.nextElementSibling as HTMLElement;
            if (f) f.style.display = 'flex';
          }}
        />
        <div className="hidden size-full items-center justify-center">
          <ImageIcon size={24} className="text-text-tertiary" />
        </div>
      </div>
    );
  }
  if (hasImageUrl && variant === 'v3') {
    return (
      <div
        className="shrink-0 overflow-hidden rounded bg-background-tertiary"
        style={{ width: 20, height: 20 }}
      >
        {/* eslint-disable-next-line @next/next/no-img-element -- Dynamic S3 presigned URLs */}
        <img src={url} alt="" className="size-full object-cover" />
      </div>
    );
  }
  if (isPartnerSource && wordmark) {
    // Wordmark partner: render the logo inline so width can expand past the
    // square frame; suppress the tinted square background which would clip it.
    return (
      <PartnerLogo
        src={partnerConfig?.logo}
        brandColor={partnerConfig?.brandColor}
        size={variant === 'v2' ? 24 : 32}
        alt={partnerConfig?.displayName}
        sourceUrl={url}
        wordmark
      />
    );
  }
  return (
    <div
      className={cn(
        'shrink-0 overflow-hidden rounded',
        variant === 'v2' ? 'mt-px size-5' : 'size-5'
      )}
    >
      {isPartnerSource ? (
        <div
          className="flex size-full items-center justify-center rounded"
          style={
            partnerConfig
              ? { backgroundColor: `${partnerConfig.brandColor}15` }
              : undefined
          }
        >
          <PartnerLogo
            src={partnerConfig?.logo}
            brandColor={partnerConfig?.brandColor}
            size={14}
            alt={partnerConfig?.displayName}
            sourceUrl={url}
          />
        </div>
      ) : isFileSource && FileIcon ? (
        <div className="flex size-full items-center justify-center">
          <FileIcon size={16} className="text-text-secondary" />
        </div>
      ) : (
        <>
          {/* eslint-disable-next-line @next/next/no-img-element -- External dynamic favicons */}
          <img
            src={getFaviconUrl(url || '')}
            alt=""
            className="size-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const f = e.currentTarget.nextElementSibling as HTMLElement;
              if (f) f.style.display = 'flex';
            }}
          />
          <div className="hidden size-full items-center justify-center bg-background-tertiary">
            <Globe size={12} className="text-text-tertiary" />
          </div>
        </>
      )}
    </div>
  );
}

function ArticleTitleLink({
  showTitle,
  title,
  isFileSource,
  url,
  handleTitleClick,
}: {
  showTitle: boolean;
  title?: string | null;
  isFileSource: boolean;
  url?: string;
  handleTitleClick?: () => void;
}) {
  if (!showTitle || !title) return null;
  if (!isFileSource && url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="min-w-0 break-words"
        // With a title handler this anchor decides what opening means — the
        // in-app viewer for some sources, the browser for others — so the
        // desktop link handler has to stand down or both happen. The literal
        // rather than the constant because this package cannot import the
        // app; `SELF_OPENING_LINK_ATTRIBUTE` in `src/tauri/externalLinkHandler`
        // owns it and is asserted against this exact string.
        {...(handleTitleClick ? { 'data-opens-itself': '' } : {})}
        onClick={(e) => {
          if (handleTitleClick) {
            e.preventDefault();
            e.stopPropagation();
            handleTitleClick();
          }
        }}
      >
        <TypographyP2
          data-testid="source-card-title"
          className="text-text-primary hover:underline"
        >
          {title}
        </TypographyP2>
      </a>
    );
  }
  if (handleTitleClick) {
    return (
      <button
        type="button"
        className="min-w-0 break-words text-start"
        onClick={handleTitleClick}
      >
        <TypographyP2
          data-testid="source-card-title"
          className="text-text-primary hover:underline"
        >
          {title}
        </TypographyP2>
      </button>
    );
  }
  return (
    <TypographyP2
      data-testid="source-card-title"
      className="break-words text-text-primary"
    >
      {title}
    </TypographyP2>
  );
}

function DocumentMetadata({
  author,
  createdAt,
}: {
  author?: string | null;
  createdAt?: string | null;
}) {
  if (!author && !createdAt) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-text-tertiary">
      {author && (
        <span className="inline-flex items-center gap-1">
          <User size={10} className="shrink-0" />
          <TypographyCaption className="text-text-tertiary">{author}</TypographyCaption>
        </span>
      )}
      {createdAt && (
        <span className="inline-flex items-center gap-1">
          <Calendar size={10} className="shrink-0" />
          <TypographyCaption className="text-text-tertiary">
            {new Date(createdAt).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </TypographyCaption>
        </span>
      )}
    </div>
  );
}

/**
 * Compact markdown components for citation snippets.
 * Everything renders at caption size (text-xs) in tertiary colour,
 * matching the SourceCard popover context. Follows the same pattern
 * as evidenceMdComponents / inlineMdComponents in markdown-components.tsx.
 */
type SnippetProps = { children?: ReactNode; className?: string };
type SnippetImgProps = { src?: string; alt?: string };

export const citationSnippetComponents = {
  p: ({ children }: SnippetProps) => (
    <p className="text-xs leading-relaxed text-text-tertiary [&+p]:mt-1.5">{children}</p>
  ),
  h1: ({ children }: SnippetProps) => (
    <p className="text-xs font-semibold text-text-tertiary">{children}</p>
  ),
  h2: ({ children }: SnippetProps) => (
    <p className="text-xs font-semibold text-text-tertiary">{children}</p>
  ),
  h3: ({ children }: SnippetProps) => (
    <p className="text-xs font-medium text-text-tertiary">{children}</p>
  ),
  h4: ({ children }: SnippetProps) => (
    <p className="text-xs font-medium text-text-tertiary">{children}</p>
  ),
  strong: ({ children }: SnippetProps) => (
    <strong className="font-medium text-text-secondary">{children}</strong>
  ),
  em: ({ children }: SnippetProps) => <em>{children}</em>,
  ul: ({ children }: SnippetProps) => (
    <ul className="my-1 list-disc pl-4 text-xs text-text-tertiary">{children}</ul>
  ),
  ol: ({ children }: SnippetProps) => (
    <ol className="my-1 list-decimal pl-4 text-xs text-text-tertiary">{children}</ol>
  ),
  li: ({ children }: SnippetProps) => <li className="text-xs">{children}</li>,
  code: ({ children }: SnippetProps) => (
    <code className="rounded bg-background-secondary px-1 py-0.5 font-mono text-xs">
      {children}
    </code>
  ),
  blockquote: ({ children }: SnippetProps) => (
    <blockquote className="my-1 border-l-2 border-text-tertiary/30 pl-2 text-xs italic text-text-tertiary">
      {children}
    </blockquote>
  ),
  table: ({ children }: SnippetProps) => (
    <div className="w-full overflow-x-auto">
      <table className="my-1 text-xs text-text-tertiary">{children}</table>
    </div>
  ),
  th: ({ children }: SnippetProps) => (
    <th className="border border-border/50 px-1.5 py-0.5 text-start text-xs font-medium">
      {children}
    </th>
  ),
  td: ({ children }: SnippetProps) => (
    <td className="border border-border/50 px-1.5 py-0.5 text-xs">{children}</td>
  ),
  // Hide broken inline images so the surrounding prose stays intact. Common
  // failure mode: partner cited-text contains markdown images whose URLs 404
  // or are blocked by CORS/CSP from our origin.
  img: ({ src, alt }: SnippetImgProps) => (
    // eslint-disable-next-line @next/next/no-img-element -- External dynamic snippet image
    <img
      src={src}
      alt={alt ?? ''}
      className="my-1 max-h-24 max-w-full rounded object-contain"
      onError={(e) => {
        e.currentTarget.style.display = 'none';
      }}
    />
  ),
};

/**
 * Props for SourceCard extend SimpleCitation with UI-specific props.
 */
interface SourceCardProps extends SimpleCitation {
  /** @deprecated Use `onTitleClick` instead. Kept for backward compat — behaves identically to `onTitleClick`. */
  onClick?: () => void;
  /** Called when the article title is clicked (e.g. analytics tracking, opening a preview). */
  onTitleClick?: () => void;
  className?: string;
  /** Index of this source within the citation group (for feedback tracking) */
  sourceIndex?: number;
  /** Chat ID for feedback tracking + EVI-1486 source-click correlation. */
  chatId?: string;
  /** Session ID for EVI-1486 source-click correlation (sessionless surfaces leave undefined). */
  sessionId?: string;
  /** Backend message ID for EVI-1486 source-click correlation. */
  messageId?: string;
  /** Show a bookmark/save-for-later action for this source (EVI-1204). */
  showSaveAction?: boolean;
  /** MOD-1726: this card's list has a local source, so a `web` one is labelled too. */
  hasLocalSources?: boolean;
  /** Visual variant. "v2" = current design (default), "v3" = redesigned card with cite score, regional chips, etc. */
  variant?: SourceCardVariant;
}

// ============================================================================
// Helper functions
// ============================================================================

/**
 * Keywords indicating journal/academic sources where date is relevant.
 */
const DATE_RELEVANT_KEYWORDS = ['journal', 'publisher', 'academic', 'research'];

/**
 * Determine whether to show the date for a given source.
 * Relevant for journals, academic sources, and guidelines.
 *
 * Engineering discussion: `pageAge` is a single field used for both publication
 * date (journals) and last-updated date (guidelines). For guidelines, clinicians
 * care about "last reviewed/updated" rather than "first published". If the backend
 * can distinguish these, we should display them differently. For now we show
 * `pageAge` for both with appropriate labelling in the UI.
 * See: docs/design-docs/source-authority-chips/guideline-date-display.md
 */
function shouldShowDate(websiteType?: string[]): boolean {
  if (!websiteType?.length) return false;
  return websiteType.some((t) => {
    const lower = t.toLowerCase();
    return (
      DATE_RELEVANT_KEYWORDS.some((kw) => lower.includes(kw)) ||
      lower.includes('guideline')
    );
  });
}

/**
 * SourceCard - Displays a citation source in the redesigned Figma format
 *
 * Typography scales by context:
 *   Out-of-session (/scribe/chat):  P Bold (16px) → P2 (14px) → Caption (12px)
 *   In-session (/scribe/session):   P2 Bold (14px) → P2 (14px) → Caption (12px)
 *
 * Layout:
 * - Row 1: Favicon + Source Name + Date (for journals/academic)
 * - Row 2: Guideline badge (if applicable)
 * - Row 3: Article Title
 * - Row 4: Cited Text Snippet (4-line max in v3, 2-line in v2)
 * - Row 5: Metadata badges + Action row (view, save, feedback)
 */
export function SourceCard({
  url,
  title,
  citedText,
  display_summary,
  source_type,
  file_name,
  source_id,
  file_key,
  document_id,
  website_name,
  website_type,
  display_name,
  pageAge,
  authoritativeness_score,
  impact_factor,
  content_type,
  // EVI-605: Backend-enriched document metadata fields
  document_title,
  document_author,
  document_page_count,
  document_created_at,
  // T15: SharePoint provenance for `user_document` citations
  source,
  // MOD-1726: where an on-prem appliance retrieved this citation from
  retrieved_from,
  hasLocalSources,
  // EVI-678: Integration partner fields
  partner_type,
  figures,
  // EVI-1105: Per-citation branding (zone-specific HealthPathways logo / colour)
  logo_url,
  brand_color,
  partner_item_id,
  // Generic metadata
  author,
  domain: domainProp,
  specialty,
  regions,
  regional_relevance,
  embed,
  text_extraction_quality,
  // UI-specific props
  onClick,
  onTitleClick,
  className,
  sourceIndex = 0,
  chatId,
  sessionId,
  messageId,
  showSaveAction,
  variant = 'v2',
}: SourceCardProps): JSX.Element {
  const isV3 = variant === 'v3';
  // Typography scales by context — larger for out-of-session (more room).
  // Route identity crosses on the `CurrentIds` port because no package `/ui`
  // zone may read `next/router`. NOT the same predicate: the base one was
  // `pathname.startsWith('/scribe/session')`, and this one differs on a session
  // route before `router.query.id` resolves (base larger, this smaller) and on
  // `/integration/epic/haiku/complete` (base smaller, this larger). Typography
  // only, and it gains the app's session-id override, which the raw pathname
  // could not see.
  const isInSession = useCurrentSessionId() !== '';
  const { t } = useTranslations();
  const {
    getPartnerConfig,
    ocrQualityTag: OcrQualityTag,
    sourceActionsComponent: SourceCardActions,
  } = useEvidenceAnswerPorts();
  const SourceName = isInSession ? TypographyP2Bold : TypographyPBold;
  const ArticleTitle = TypographyP2;

  // Merge legacy onClick with onTitleClick (onTitleClick takes precedence)
  const handleTitleClick = onTitleClick ?? onClick;
  // Get type-specific display properties
  const {
    fullDisplayName,
    cardDisplayName,
    isFileSource,
    isImageSource,
    FileIcon,
    showTitle,
    sourceLabel,
  } = useCitationDisplay({
    url,
    title,
    citedText,
    source_type,
    file_name,
    source_id,
    file_key,
    document_id,
    document_title,
    website_name,
    display_name,
    domain: domainProp,
  });
  const sourceCardName = cardDisplayName || fullDisplayName;

  // EVI-605: Check if we have document metadata to display
  const isUserDocument = source_type === 'user_document';
  const hasDocumentMetadata = isUserDocument && (document_author || document_created_at);
  // EVI-1625: surface the backend-recorded text-extraction/OCR quality tag on
  // cited library documents. Port 8 is ONE component slot rather than a
  // resolver: the app side reads `text_extraction_quality` behind a hook that
  // consults a library feature flag, and renders nothing when the document
  // extracted cleanly or is not a user document.
  // T15: Surface SharePoint provenance as a small sub-label below the
  // title for `user_document` citations whose underlying file was
  // ingested from SharePoint. Aryan: "when heidi quotes a document from
  // a sharepoint collection in an answer, should the citation say
  // 'from SharePoint'? Tarek's design says yes. do as tareks's design".
  // We only render for user_document citations — web / partner / image
  // citations don't share the source space, so the label would be a
  // category error.
  const isSharepointUserDocument = isUserDocument && source === 'sharepoint';

  // EVI-678: Integration partner detection and config
  const isPartnerSource = isPartnerCitation(source_type);
  const partnerNejmFiguresEnabled = useIsPartnerNejmFiguresEnabled();
  const showPartnerFigures = isPartnerSource && partnerNejmFiguresEnabled;
  const basePartnerConfig =
    isPartnerSource && partner_type ? getPartnerConfig(partner_type) : undefined;
  // EVI-1105: Prefer per-citation branding (zone-specific HealthPathways logo /
  // colour) over the partner-level catalog values. `nameInLogo` is preserved via
  // the spread — wordmark mode stays partner-keyed, not per-citation.
  const partnerConfig = basePartnerConfig
    ? {
        ...basePartnerConfig,
        logo: logo_url ?? basePartnerConfig.logo,
        brandColor: brand_color ?? basePartnerConfig.brandColor,
      }
    : undefined;

  const hasImageUrl = isImageSource && url;
  const domain = domainProp || getDomainFromUrl(url || '');

  // EVI-1570: partner sources show only the plain-text display_summary (never the
  // raw-HTML citedText) — nothing when it's absent. Non-partner sources keep citedText.
  // Partner summaries may still arrive with HTML markup; strip tags before display.
  const cardText = resolvePartnerSourceSnippetText({
    displaySummary: display_summary,
    isPartnerSource,
    fallbackSnippet: citedText,
  });
  const renderSummaryAsPlainText = Boolean(display_summary);

  // Date display: for journals, academic sources, and guidelines
  const showDate =
    !isFileSource && !isPartnerSource && !!pageAge && shouldShowDate(website_type);

  // Guideline badge: on/off based on website_type
  const showGuideline =
    !isFileSource && !isPartnerSource && isGuidelineSource(website_type);

  // Authority tier badge (only metadata badge shown in tooltip;
  // content_type, orgType and impact_factor are reserved for the reference list)
  // Jurisdiction labelling (EV-022): tri-state regional / global /
  // outOfRegion relative to the user's region. The BE owns the
  // classification (`regional_relevance`) and only emits it when it could
  // resolve the user's country, so an absent field means "no chip" — used
  // for anonymous / pre-onboarding users and for older citations from
  // before the BE field shipped.
  const jurisdiction =
    !isFileSource && !isPartnerSource ? getJurisdiction(regional_relevance) : null;
  const isRegional = jurisdiction?.state === 'inRegion';

  // Build source object for actions
  const sourceForActions: SimpleCitation = {
    url,
    title,
    citedText,
    source_type,
    file_name,
    source_id,
    file_key,
    document_id,
    website_name,
    website_type,
    display_name,
    pageAge,
    authoritativeness_score,
    impact_factor,
    content_type,
    document_title,
    document_author,
    document_page_count,
    document_created_at,
    source,
    partner_type,
    partner_item_id,
    figures,
    author,
    domain: domainProp,
    specialty,
    regions,
    regional_relevance,
    embed,
    // Carried so the port-8 slot can read it: the tag used to be resolved from
    // the destructured prop, and the slot is handed this citation instead.
    text_extraction_quality,
  };

  // V3: determine which chip(s) to show in the title row. Guideline and
  // jurisdiction are separate dimensions — a source can be both. For the
  // positive in-jurisdiction case we still merge into a single "Regional
  // guideline" badge to save space; for international/foreign sources we show
  // the guideline badge (if any) alongside a distinct jurisdiction chip so
  // out-of-jurisdiction guidelines are clearly flagged (EV-022).
  const showCombinedRegionalGuideline = isV3 && showGuideline && isRegional;
  const showGuidelineBadge = isV3 && showGuideline && !showCombinedRegionalGuideline;
  const showJurisdictionBadge = isV3 && !!jurisdiction && !showCombinedRegionalGuideline;

  // EVI-1105: suppress the sibling name only when rendering a per-citation zone
  // logo (HealthPathways CHP/HHP wordmark). The neutral partner logo keeps its label.
  const hidePartnerName = !!logo_url;

  if (isV3) {
    return (
      <div className={cn('flex w-full min-w-0 flex-col gap-4 text-start', className)}>
        {/* Source content group — 8px internal gap (Figma node 7520:32516) */}
        <div className="flex flex-col gap-2">
          {/* Row 1: Favicon + Source Name + Chip */}
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            <SourceIcon
              isPartnerSource={isPartnerSource}
              partnerConfig={partnerConfig}
              isFileSource={isFileSource}
              FileIcon={FileIcon}
              url={url}
              hasImageUrl={!!hasImageUrl}
              file_name={file_name}
              variant="v3"
              wordmark={hidePartnerName}
            />

            {!hidePartnerName && (
              <SourceName
                data-testid="source-card-name"
                className="min-w-0 break-words text-text-primary"
                style={partnerConfig ? { color: partnerConfig.brandColor } : undefined}
              >
                {isPartnerSource && partnerConfig
                  ? partnerConfig.displayName
                  : sourceCardName}
              </SourceName>
            )}
            {showDate && (
              <SourceName className="shrink-0 text-text-primary">
                &middot; {pageAge}
              </SourceName>
            )}

            {/* V3: Badge chip in title row — source type, guideline, jurisdiction, or partner */}
            {isPartnerSource && <IntegrationPartnerBadge />}
            {isFileSource && sourceLabel && (
              <Badge color="neutral" size="sm">
                {sourceLabel}
              </Badge>
            )}
            <OcrQualityTag citation={sourceForActions} />
            {showCombinedRegionalGuideline && (
              <Badge color="blue" size="sm" data-testid="source-guideline-badge">
                <MapPinned />
                {t('chat.regionalGuideline')}
              </Badge>
            )}
            {showGuidelineBadge && (
              <Badge color="blue" size="sm" data-testid="source-guideline-badge">
                <CheckCircle2 />
                {t('chat.guideline')}
              </Badge>
            )}
            {showJurisdictionBadge && jurisdiction && (
              <JurisdictionBadge jurisdiction={jurisdiction} />
            )}
          </div>

          <ArticleTitleLink
            showTitle={showTitle}
            title={title}
            isFileSource={isFileSource}
            url={url}
            handleTitleClick={handleTitleClick}
          />

          {isSharepointUserDocument && (
            <TypographyCaption
              className="text-text-tertiary"
              data-testid="citation-from-sharepoint"
            >
              {t('chat.citation.fromSharepoint')}
            </TypographyCaption>
          )}
          <RetrievedFromLabel
            retrievedFrom={retrieved_from}
            hasLocalSources={hasLocalSources}
          />

          {hasDocumentMetadata && (
            <DocumentMetadata author={document_author} createdAt={document_created_at} />
          )}

          {/* Row 3: Cited Text Snippet — 4-line clamp to show ~500 char snippets */}
          {cardText && (
            <div
              data-testid="source-card-snippet"
              className="line-clamp-4 text-xs leading-relaxed text-text-tertiary [&>*]:contents"
            >
              {renderSummaryAsPlainText ? (
                cardText
              ) : (
                <Streamdown
                  mode="static"
                  controls={{ table: true }}
                  components={citationSnippetComponents}
                  rehypePlugins={streamdownRehypePlugins}
                >
                  {cardText}
                </Streamdown>
              )}
            </div>
          )}

          {showPartnerFigures && figures?.[0] && (
            <PartnerFigurePreview key={figures[0].id} figure={figures[0]} />
          )}

          {!isFileSource && !title && !cardText && (
            <ArticleTitle className="break-words text-text-tertiary">
              {domain}
            </ArticleTitle>
          )}
        </div>

        {/* Metadata + actions — single row (Figma node 7520:33071) */}
        <div className="flex items-center">
          {!isFileSource && !isPartnerSource && (
            <CiteScoreBadge score={authoritativeness_score} citeScore={impact_factor} />
          )}

          <SourceCardActions
            source={sourceForActions}
            index={sourceIndex}
            chatId={chatId}
            sessionId={sessionId}
            messageId={messageId}
            className="ml-auto"
            feedbackPopoverSide="right"
            showSaveAction={showSaveAction}
            variant="v3"
          />
        </div>
      </div>
    );
  }

  // ============================================================================
  // V2 layout (original)
  // ============================================================================
  return (
    <div className={cn('flex w-full min-w-0 items-start gap-2 text-start', className)}>
      <SourceIcon
        isPartnerSource={isPartnerSource}
        partnerConfig={partnerConfig}
        isFileSource={isFileSource}
        FileIcon={FileIcon}
        url={url}
        hasImageUrl={!!hasImageUrl}
        file_name={file_name}
        variant="v2"
        wordmark={hidePartnerName}
      />

      {/* Content */}
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        {/* Row 1: Source Name + Date + Guideline badge */}
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          {!hidePartnerName && (
            <SourceName
              className="min-w-0 break-words text-text-primary"
              style={partnerConfig ? { color: partnerConfig.brandColor } : undefined}
            >
              {isPartnerSource && partnerConfig
                ? partnerConfig.displayName
                : sourceCardName}
            </SourceName>
          )}
          {isPartnerSource && <IntegrationPartnerBadge />}
          {showDate && (
            <SourceName className="shrink-0 text-text-primary">
              &middot; {pageAge}
            </SourceName>
          )}
          {showGuideline && (
            <Badge color="blue" size="sm">
              <CheckCircle2 />
              {t('chat.guideline')}
            </Badge>
          )}
          <OcrQualityTag citation={sourceForActions} />
        </div>

        <ArticleTitleLink
          showTitle={showTitle}
          title={title}
          isFileSource={isFileSource}
          url={url}
          handleTitleClick={handleTitleClick}
        />
        {isFileSource && sourceLabel && (
          <ArticleTitle className="text-text-tertiary">
            {sourceLabel}
            {getFileTypeLabel(file_name) && ` (${getFileTypeLabel(file_name)})`}
          </ArticleTitle>
        )}

        {isSharepointUserDocument && (
          <TypographyCaption
            className="text-text-tertiary"
            data-testid="citation-from-sharepoint"
          >
            {t('chat.citation.fromSharepoint')}
          </TypographyCaption>
        )}
        <RetrievedFromLabel
          retrievedFrom={retrieved_from}
          hasLocalSources={hasLocalSources}
        />

        {hasDocumentMetadata && (
          <DocumentMetadata author={document_author} createdAt={document_created_at} />
        )}

        {/* Row 4: Cited Text Snippet - capped to 2 lines with ellipsis */}
        {cardText && (
          <div
            data-testid="source-card-snippet"
            className="line-clamp-2 text-xs leading-relaxed text-text-tertiary [&>*]:contents"
          >
            {renderSummaryAsPlainText ? (
              cardText
            ) : (
              <Streamdown
                mode="static"
                controls={{ table: true }}
                components={citationSnippetComponents}
                rehypePlugins={streamdownRehypePlugins}
              >
                {cardText}
              </Streamdown>
            )}
          </div>
        )}

        {showPartnerFigures && figures?.[0] && (
          <PartnerFigurePreview key={figures[0].id} figure={figures[0]} />
        )}

        {/* Show domain if no title and no cited text (only for web sources) */}
        {!isFileSource && !title && !cardText && (
          <ArticleTitle className="break-words text-text-tertiary">{domain}</ArticleTitle>
        )}

        {/* Row 5: Metadata badges + Actions (single row) */}
        <div className="mt-1.5 flex items-center gap-2">
          {!isFileSource && !isPartnerSource && (
            <AuthorityTierBadge score={authoritativeness_score} size="sm" />
          )}
          {jurisdiction && <JurisdictionBadge jurisdiction={jurisdiction} />}

          <SourceCardActions
            source={sourceForActions}
            index={sourceIndex}
            chatId={chatId}
            sessionId={sessionId}
            messageId={messageId}
            className="ml-auto"
            feedbackPopoverSide="right"
            showSaveAction={showSaveAction}
          />
        </div>
      </div>
    </div>
  );
}
